import { Frame, Img, DeckViewer, DeckPrint } from "../headliner/DeckShell";
import { CinematicCover, CINE_ASSETS } from "../headliner/CinematicCover";
import { WhatItIs } from "../headliner/WhatItIs";
import { TEAM_ASSETS } from "../headliner/TeamAndPartners";
import { WhoBehind, WHO_ASSETS } from "../headliner/WhoBehind";
import { Bergen, Place, Room, WhyNow } from "../headliner/HeadlinerDeck";
import "../headliner/headliner.css";
import "../lawpartner/law.css";
import "../buildpartner/build.css";
import "./vision-deck.css";

/* Vision-decket «The Next Shift» — hva Norwegian AI Summit er, for alle som ikke er en partnerkategori: talere, investorer, Bergen,
   presse, rådgivere og oss selv. Samme 16:9-ark som partnerdeckene (papir, Kevin + Grieghallen i mediekolonnen, gate/PDF/OG/anonyme lenker).
   Varianter via kategori-feltet: "" = general · speaker = «We want you in the room» · internal = What must be true + Road to April
   (internal vises kun i admin-forhåndsvisning; offentlig lenke m/ internal faller tilbake til general). Statusord er de samme som i
   partnerdeckene — ingen taler, partner eller leverandør påstås bekreftet uten at det er sant. */
const VARIANTS = { "": "Vision · 2027", speaker: "Speaker invitation", internal: "Internal · master" };
export const variantOf = (category, publicMode) => (category === "internal" && publicMode ? "" : (category in VARIANTS ? category : ""));

const KEVIN = "/images/headliner/kevin-src-hi.webp";

/* 01 Cover — felles kinematisk cover (CinematicCover): Grieghallen full-bleed, Kevin i høyre tredjedel, indeksrad. */
const Cover = ({ mottaker, category }) => (
  <CinematicCover
    testid="vd-cover"
    label={VARIANTS[category] || VARIANTS[""]}
    title="The Next Shift."
    sub="One day. One room. The people deciding how Norway will use AI."
    index={[
      ["Date", "13 April 2027"],
      ["Venue", "Grieghallen, Bergen"],
      [mottaker ? "Prepared for" : "Audience", mottaker || "700 seats · invitation-led", mottaker ? "prep" : "", "vd-cover-prep"],
    ]}
  />
);

/* 05 The headliner — Kevin som foreslått åpningskeynote. Statusmodell: «Availability confirmed» = dato bekreftet ledig, ikke kontrakt.
   Buen (09:30/10:20/11:00) er foreslått design innenfor de 2,5 timene, ikke en signert kjøreplan. */
const KEVIN_ARC = [
  ["09:30", "Opening keynote", "Main stage. The whole room in the same seat from the first minute of the day."],
  ["10:20", "The Conversation", "Kevin O'Leary in two chairs with the CEO of the Headliner Partner, moderated by the summit."],
  ["11:00", "The Executive Room", "15–20 invited leaders. Closed room, no recording, Chatham House Rule."],
  ["2.5 h", "On site", "Keynote, conversation and executive access — designed as one arc, not three bookings."],
];

const VHeadliner = ({ nr, mottaker }) => (
  <Frame nr={nr} label="The headliner" mottaker={mottaker} className="hd-headliner vd-kevin hd-light">
    <div className="hd-photo"><Img src={KEVIN} alt="Kevin O'Leary" draggable={false} /></div>
    <div className="hd-body">
      <h2 className="hd-name" data-testid="vd-kevin-h">Kevin O'Leary</h2>
      <p className="vd-kevin-role" data-testid="vd-kevin-role">Proposed opening keynote · Investor · Entrepreneur · Shark Tank</p>
      <p className="hd-lead" data-testid="vd-kevin-lead">One of the world's most recognisable business voices, proposed to open Norwegian AI Summit at 09:30 on 13 April 2027. Not a celebrity slot — a headliner who talks about capital, risk and what AI is doing to the companies he invests in.</p>
      <p className="vd-kevin-arc" data-testid="vd-kevin-arc-label">Proposed arc · 2.5 hours on site</p>
      <div className="hd-blocks" data-testid="vd-kevin-blocks">
        {KEVIN_ARC.map(([t, l, d]) => <div className="hd-block" key={l}><span className="t">{t}</span><span className="l">{l}</span><span className="d">{d}</span></div>)}
      </div>
      <p className="hd-line" data-testid="vd-kevin-line"><span className="gul">Availability confirmed</span> for 13 April 2027 — date confirmed available with his team; not yet contracted.</p>
    </div>
  </Frame>
);

/* 06 The programme — listen vi jobber, med én konsekvent statusmodell (legend nederst). Ingen honorar, ingen byrå-navn.
   Mottakeren av en speaker-invitasjon får Voices-sliden i stedet (ser ikke seg selv som prospekt). */
const ROSTER = [
  ["Kevin O'Leary", "Investor · Entrepreneur · Shark Tank", "Business leadership", "Proposed headliner · Availability confirmed", "on"],
  ["Silvija Seres", "Technologist · Investor", "Capital · Technology", "Availability confirmed", "on"],
  ["Sebastian Thrun", "Founder of Google X · Udacity · Waymo", "Frontier technology", "Target", ""],
  ["Zack Kass", "AI futurist · former Head of Go-To-Market, OpenAI", "Frontier technology", "Target", ""],
  ["Sol Rashidi", "The first enterprise Chief AI Officer · Author", "Enterprise AI", "Target", ""],
  ["Yoky Matsuoka", "CEO, Panasonic Well · Co-founder, Google X", "Physical AI", "Target", ""],
  ["Jonathan Brill", "Futurist-in-Residence, Amazon · Author", "Business transformation", "Target", ""],
  ["Keith Ferrazzi", "Executive team coach · Author of Never Eat Alone", "Leadership", "Target", ""],
  ["Ian Beacraft", "CEO & Chief Futurist, Signal & Cipher", "Future of work", "Target", ""],
  ["Inga Strümke", "AI researcher, NTNU · Author", "AI research", "Target", ""],
  ["Nicolai Tangen", "CEO, Norges Bank Investment Management", "Capital", "Target", ""],
];

const isSelf = (name, mottaker) => !!mottaker && name.toLowerCase() === mottaker.trim().toLowerCase();

const Programme = ({ nr, mottaker }) => (
  <Frame nr={nr} label="The programme" mottaker={mottaker} className="vd-prog hd-light">
    <h2 className="hd-h2" data-testid="vd-prog-h">Proposed programme.<br />Curated, one voice at a time.</h2>
    <p className="vd-prog-sub" data-testid="vd-prog-sub"><b>Speakers shown are confirmed, in conversation or targets. Final programme subject to confirmation.</b> Business leadership, AI research, frontier technology, capital and policy — on one stage, no parallel tracks. Names are announced when they are signed.</p>
    <ol className="vd-roster" data-testid="vd-roster">
      {ROSTER.map(([n, r, v, st, cls], i) => {
        const self = isSelf(n, mottaker);
        return (
          <li key={n} className={self ? "self" : cls} style={{ "--i": i }} data-testid={`vd-roster-${i + 1}`}>
            <span className="i">{String(i + 1).padStart(2, "0")}</span>
            <span className="n">{n}</span>
            <span className="r">{r}</span>
            <span className="v">{v}</span>
            <span className="st">{self ? "This invitation" : st}</span>
          </li>
        );
      })}
    </ol>
    <p className="vd-prog-foot" data-testid="vd-prog-foot"><b>Confirmed</b> — contracted · <b>Availability confirmed</b> — date confirmed available; not yet contracted · <b>In conversation</b> — active dialogue · <b>Target</b> — proposed / outreach planned.</p>
  </Frame>
);

/* 06b The programme · speaker-variant — kurateringsfilosofien, ikke pipelinen. Mottakeren skal ikke se seg selv som et prospekt. */
const VOICES = [
  ["Business leadership", "CEOs and board members on what AI changes in how companies are led, financed and run."],
  ["AI research", "The researchers who know what the models can and cannot do — and what comes next."],
  ["Frontier technology", "The labs and platforms building the frontier — on the main stage, not in a booth."],
  ["Capital", "Investors and owners on where the money goes, and what they now expect from the companies they back."],
  ["Policy", "Regulators and policymakers on the rules that will shape the decade."],
];

const Voices = ({ nr, mottaker }) => (
  <Frame nr={nr} label="The programme" mottaker={mottaker} className="vd-prog vd-voices hd-light">
    <h2 className="hd-h2" data-testid="vd-voices-h">A programme in curation.<br />Five voices, one stage.</h2>
    <p className="vd-prog-sub" data-testid="vd-voices-sub">Business leadership, AI research, frontier technology, capital and policy — on one stage, no parallel tracks. Every session is prepared and moderated by the summit. Names are announced when they are signed; the final programme is subject to confirmation.</p>
    <ol className="vd-voices-grid" data-testid="vd-voices-grid">
      {VOICES.map(([t, d], i) => <li key={t}><span className="n">0{i + 1}</span><span className="t">{t}</span><span className="d">{d}</span></li>)}
    </ol>
    <p className="vd-voices-foot" data-testid="vd-voices-foot"><b>Availability confirmed so far</b> — Kevin O'Leary, proposed opening keynote · Silvija Seres. Dates confirmed available, not yet contracted. The rest of the programme is being curated now.</p>
  </Frame>
);

/* 07 Why Norway — for alle, ikke for laben. Verifiserte fakta (Stargate Norway/Narvik, Microsoft 2019, Google Skien 2024). */
const NORWAY = [
  ["Compute", "Stargate Norway — OpenAI's first European site under OpenAI for Countries — is being built outside Narvik by Nscale and Aker: 230 MW initially, 100,000 NVIDIA GPUs targeted by the end of 2026."],
  ["Cloud", "Microsoft has operated two Norwegian cloud regions since 2019. Google broke ground on a €600 million data centre in Skien in 2024."],
  ["Power", "Hydropower, a cool climate and an electricity mix that is almost entirely renewable — the reason the frontier's infrastructure is arriving here."],
  ["Market", "A high-trust, high-adoption economy: a sovereign wealth fund, a large public sector, energy, finance, maritime and industry — the sectors in the room."],
];

const VWhyNorway = ({ nr, mottaker }) => (
  <Frame nr={nr} label="Why Norway" mottaker={mottaker} className="hd-cant vd-norway hd-light">
    <h2 className="hd-h2" data-testid="vd-norway-h">The frontier's infrastructure<br />is arriving in Norway.</h2>
    <ul className="hd-ledger" data-testid="vd-norway-points">
      {NORWAY.map(([k, v]) => <li key={k}><span className="k">{k}</span><span className="v">{v}</span></li>)}
    </ul>
    <p className="hd-cant-foot" data-testid="vd-norway-foot">The infrastructure is arriving. The decisions about what runs on it will be made by the people in this room.</p>
  </Frame>
);

/* 08 The day — dagsreisen uten partner-markører */
const DAG = [
  ["09:00", "Arrival"], ["09:30", "Opening keynote · Kevin O'Leary (proposed)", true], ["10:20", "The Conversation"], ["11:30", "Leadership programme"],
  ["13:00", "AI Experience · The Build Lab"], ["14:30", "The live build", true], ["16:00", "Closing keynote"], ["18:30", "Summit Dinner"],
];

const VDay = ({ nr, mottaker }) => (
  <Frame nr={nr} label="The day" mottaker={mottaker} className="hd-day hd-light">
    <h2 className="hd-h2" data-testid="vd-day-h">This is not a sequence of talks.<br />It is a designed day.</h2>
    <ol className="hd-timeline" data-testid="vd-timeline">
      {DAG.map(([t, l, hi]) => (
        <li key={t} className={hi ? "hi" : ""}><span className="t">{t}</span><i className="pt" /><span className="l">{l}</span></li>
      ))}
    </ol>
    <p className="hd-dayfoot">One programme, one room, all day — the whole audience in the same seat. Times are proposed and set with the programme.</p>
  </Frame>
);

/* 09 Beyond the stage — konseptbilder i deckets palett (varmt, ingen lilla): opplevelsesflaten, foajeen, dinner */
const BEYOND = [
  ["AI Experience", "See it. Test it. Meet the people building it.", "/images/vision-expo-v3.webp"],
  ["The network", "CEOs · founders · investors · researchers · policymakers — in Grieghallen's foyer, with the city outside.", "/images/vision-networking-v3.webp"],
  ["Summit Dinner", "The conversation continues after the stage closes.", "/images/vision-dinner-v4.webp"],
];

const VBeyond = ({ nr, mottaker }) => (
  <Frame nr={nr} label="Beyond the stage" mottaker={mottaker} className="hd-beyond hd-light">
    <h2 className="hd-h2" data-testid="vd-beyond-h">The summit is bigger<br />than the stage.</h2>
    <div className="hd-frags" data-testid="vd-beyond-frags">
      {BEYOND.map(([n, d, img]) => (
        <figure className="hd-frag" key={n}>
          <div className="ph"><Img src={img} alt={n} draggable={false} /></div>
          <figcaption><span className="n">{n}</span><span className="d">{d}</span></figcaption>
        </figure>
      ))}
    </div>
    <span className="vd-concept" data-testid="vd-beyond-cap">Concept visualisations</span>
  </Frame>
);

/* 10 Built for reach — ambisjon uten oppfunnede tall */
const REACH = [
  ["Announcement", "The programme, the headliner and the partners are announced together — one national news moment"],
  ["Within 24 hours", "Flagship clips from the keynote, The Conversation and the live build — cut for the channels that matter"],
  ["On demand", "Full sessions after the summit; every speaker's moment as a stand-alone cut"],
  ["Through the year", "LinkedIn, YouTube and press assets — and an editorial series that keeps the conversation going until the next one"],
];

const VReach = ({ nr, mottaker }) => (
  <Frame nr={nr} label="Built for reach" mottaker={mottaker} className="hd-reach vd-reach hd-light">
    <h2 className="hd-h2" data-testid="vd-reach-h">Built for 700.<br />Designed to travel further.</h2>
    <ul className="hd-reach-list" data-testid="vd-reach-list">
      {REACH.map(([k, d]) => <li key={k}><span className="k">{k}</span><span className="d">{d}</span></li>)}
    </ul>
    <div className="hd-reach-ph"><Img src="/images/vision-broadcast.webp" alt="Broadcast production at the main stage — concept visualisation" draggable={false} /></div>
    <p className="hd-reach-foot">The distribution plan is built with the programme. Reach figures follow when they are real — we do not invent them.</p>
  </Frame>
);

/* 12 The standard — reglene som gjør rommet verdt å være i */
const STANDARD = [
  ["No sales pitches on stage", "Every session is prepared, moderated and briefed by the summit. Partners speak for themselves — and earn the room, not buy it."],
  ["Invitation-led", "700 seats, curated across sectors: leadership, technology, capital, policy. Not a ticket you buy — a seat you are given."],
  ["One room, one programme", "No parallel tracks. Everyone hears the same argument, sees the same build and leaves with the same references."],
  ["Editorial independence", "The summit owns the programme. Partners own their moments. No one stands behind anyone else's opinions."],
];

const Standard = ({ nr, mottaker }) => (
  <Frame nr={nr} label="The standard" mottaker={mottaker} className="hd-exec hd-light">
    <h2 className="hd-h2 big" data-testid="vd-standard-h">Not another conference.<br />A standard for the room.</h2>
    <p className="hd-exec-sub">Norway has conferences. It does not yet have the room where its leaders set direction on AI together. The rules below are how everything that follows in this deck is curated.</p>
    <div className="hd-exec-grid" data-testid="vd-standard-points">
      {STANDARD.map(([t, d]) => <div className="hd-point" key={t}><span className="t">{t}</span><span className="d">{d}</span></div>)}
    </div>
    <p className="hd-exec-foot">Prepared, curated, independent — the standard is the product.</p>
  </Frame>
);

/* 13 The partnership architecture — én partner per kategori; statuser vi kan stå inne for */
const ARCH = [
  ["Headliner Partner", "The position around the international headliner: opening keynote presented by, The Conversation, The Executive Room. One institution — category exclusive.", "Position open"],
  ["Frontier Partner", "One of the leading AI labs or hyperscalers: the Frontier Keynote, The Adoption Room, the Norway Index. Exclusive on the partner programme.", "Position open"],
  ["Build Partner", "One AI-native building platform: the live build on the main stage and The Build Lab. Vibe coding, category exclusive.", "Position open"],
  ["AI Law & Policy Partner", "One law firm: the Rules Segment and the Board Briefing — the legal dimension of every material AI decision.", "Position open"],
  ["Summit Dinner · Broadcast · AI Experience", "Hospitality, distribution and the floor — defined with the programme.", "Planned"],
];

const Architecture = ({ nr, mottaker }) => (
  <Frame nr={nr} label="Appendix · The partnership architecture" mottaker={mottaker} className="vd-arch hd-light">
    <h2 className="hd-h2" data-testid="vd-arch-h">One partner per category.<br />No competing logos in the room.</h2>
    <p className="vd-arch-sub" data-testid="vd-arch-sub">Partners do not sponsor the summit — each owns one defined position in the programme, with the rights and the responsibility that follow. Founding · Headliner · Frontier · Build · AI Law &amp; Policy · Dinner · Broadcast.</p>
    <ul className="vd-arch-list" data-testid="vd-arch-list">
      {ARCH.map(([t, d, st]) => (
        <li key={t}><span className="t">{t}</span><span className="d">{d}</span><span className={`st ${st === "Planned" ? "" : "open"}`}>{st}</span></li>
      ))}
    </ul>
    <p className="bd-foot" data-testid="vd-arch-foot">Partners are announced together with the programme. Positions are confirmed by 1 October 2026.</p>
  </Frame>
);

/* 14 Who is behind it — delt med Master-decket (team + Delivered with Unite · Cure · Grieghallen) */
const Team = ({ nr, mottaker }) => <WhoBehind nr={nr} mottaker={mottaker} label="The team" testid="vd-who" />;

/* 13 Invitation (speaker) — en rolle er designet for mottakeren, ikke en konferanseslot. Fornavn i tiltalen når vi har et navn. */
const SPEAKER_OFFER = [
  ["On stage", "A substantive role in the programme — a prepared, moderated session in front of 700 of Norway's leaders, builders, investors, researchers and policymakers. Not a conventional conference slot."],
  ["In the programme", "Help us frame the questions Norway should actually be discussing in 2027 — before the programme is locked."],
  ["Around the room", "Part of a curated gathering of the people deciding how AI is adopted in Norway: the briefing the evening before, the Summit Dinner, the room itself."],
  ["Afterwards", "Your session as a stand-alone cut, cleared for your own use — and part of the summit's editorial series."],
];

const firstName = (m) => (m || "").trim().split(/\s+/)[0];

const Invite = ({ nr, mottaker }) => (
  <Frame nr={nr} label="Invitation" mottaker={mottaker} className="vd-end hd-light">
    <h2 className="hd-h2" data-testid="vd-invite-h">{mottaker ? <>{firstName(mottaker)}, we would like you<br />to help shape the room.</> : <>We want you<br />in the room.</>}</h2>
    <p className="vd-end-p" data-testid="vd-invite-p">{mottaker ? "We are writing to you because the programme needs your perspective — not for a slot, but for a role in how the day is framed." : "We are curating the programme for 13 April 2027 — one prepared session at a time."}</p>
    <ul className="hd-ledger vd-ledger" data-testid="vd-invite-offer">
      {SPEAKER_OFFER.map(([k, v]) => <li key={k}><span className="k">{k}</span><span className="v">{v}</span></li>)}
    </ul>
    <p className="vd-end-next" data-testid="vd-invite-next"><span className="k">Next step</span>A 30-minute conversation. We bring the programme; you bring your perspective.</p>
    <div className="hd-contact vd-contact" data-testid="vd-contact"><span className="n">Martin Kviteberg · CEO, Norwegian AI Summit</span><span className="e">martin@norwegianaisummit.no</span></div>
  </Frame>
);

const WAYS = [
  ["Partner", "One partner per category — headliner, frontier, build, law & policy, dinner, broadcast. Positions confirmed by 1 October 2026."],
  ["Speak", "A prepared session on the main stage — business leadership, research, frontier technology, capital or policy."],
  ["Attend", "700 invitation-led seats. Nominate the people in your organisation who decide on AI."],
  ["Build with us", "Bergen institutions, media and networks that want the room to exist — the summit is built with the city, not just in it."],
];

const Join = ({ nr, mottaker }) => (
  <Frame nr={nr} label="Join us" mottaker={mottaker} className="vd-end hd-light">
    <h2 className="hd-h2" data-testid="vd-join-h">Join us<br />in building it.</h2>
    <p className="vd-end-p" data-testid="vd-join-p">{mottaker ? `${mottaker} — we would like to explore how you take part in Norwegian AI Summit 2027.` : "Norwegian AI Summit 2027 is being built now. Four ways in:"}</p>
    <ul className="hd-ledger vd-ledger" data-testid="vd-join-ways">
      {WAYS.map(([k, v]) => <li key={k}><span className="k">{k}</span><span className="v">{v}</span></li>)}
    </ul>
    <p className="vd-end-next" data-testid="vd-join-next"><span className="k">Next step</span>A 30-minute conversation. We bring the programme; you bring the ambition.</p>
    <div className="hd-contact vd-contact" data-testid="vd-contact"><span className="n">Martin Kviteberg · CEO, Norwegian AI Summit</span><span className="e">martin@norwegianaisummit.no</span></div>
  </Frame>
);

/* Internal: What must be true + Road to 13 April */
const MUST = [
  ["The headliner", "Kevin O'Leary contracted for 13 April 2027 — 2.5 hours on site: keynote, The Conversation, The Executive Room."],
  ["The venue", "Grieghallen confirmed and booked; production partner signed; the day's technical plan approved."],
  ["The partners", "Four category partners confirmed by 1 October 2026 — headliner, frontier, build, law & policy. One per category, no exceptions."],
  ["The room", "700 seats filled by invitation: leadership, technology, capital, policy. Nominations open with the announcement."],
  ["The programme", "Every session prepared and briefed. No sales pitches on stage. The live build rehearsed with production infrastructure."],
  ["The reach", "Broadcast and content plan built with the programme; flagship clips within 24 hours of every main-stage moment."],
];

const MustBeTrue = ({ nr, mottaker }) => (
  <Frame nr={nr} label="Internal · What must be true" mottaker={mottaker} className="vd-must hd-light">
    <h2 className="hd-h2" data-testid="vd-must-h">What must be true<br />on 13 April 2027.</h2>
    <ol className="vd-must-list" data-testid="vd-must-list">
      {MUST.map(([t, d], i) => <li key={t}><span className="n">0{i + 1}</span><span className="t">{t}</span><span className="d">{d}</span></li>)}
    </ol>
  </Frame>
);

const ROAD = [
  ["Now → 1 Oct 2026", "Partner positions", "Decks out, working sessions, term sheets. Four categories confirmed."],
  ["Q4 2026", "Design together", "The Conversation, The Executive Room, the live build, the guest list — built with the partners."],
  ["January 2027", "Announcement", "Programme, headliner and partners announced together. Nominations open."],
  ["Feb – Mar 2027", "The room", "700 seats by invitation. Briefings, rehearsals, production plan locked."],
  ["13 April 2027", "The day", "Grieghallen, Bergen. One day. One room."],
];

const Road = ({ nr, mottaker }) => (
  <Frame nr={nr} label="Internal · Road to 13 April" mottaker={mottaker} className="vd-road hd-light">
    <h2 className="hd-h2" data-testid="vd-road-h">Road to 13 April.</h2>
    <ol className="vd-road-list" data-testid="vd-road-list">
      {ROAD.map(([w, t, d]) => <li key={w}><span className="w">{w}</span><span className="t">{t}</span><span className="d">{d}</span></li>)}
    </ol>
    <p className="bd-foot">Internal working document — statuses in this deck are the ones we can stand behind today, nothing more.</p>
  </Frame>
);

/* Fem akter (brukerens dramaturgi): THESIS → WHY NOW → WHY NORWAY → THE ROOM → THE RULES → THE PEOPLE → THE EXPERIENCE → CREDIBILITY → INVITATION.
   Speaker (15): Voices i stedet for roster, personlig Invite, ingen partnerinventar. General (16): + partnerarkitektur som appendix.
   Internal (18): + What must be true + Road. */
const FLOW = [Cover, WhatItIs, WhyNow, VWhyNorway, Room, Standard, VHeadliner];
const TAIL = [VDay, VBeyond, VReach, Bergen, Team, Place];
const SPEAKER = [...FLOW, Voices, ...TAIL, Invite];
const GENERAL = [...FLOW, Programme, ...TAIL, Join, Architecture];
const INTERNAL = [...GENERAL, MustBeTrue, Road];

const slidesFor = (publicMode) => (mottaker, category) => {
  const v = variantOf(category, publicMode);
  return v === "speaker" ? SPEAKER : v === "internal" ? INTERNAL : GENERAL;
};

const ASSETS = [
  KEVIN, ...CINE_ASSETS, "/images/what-room-v4.webp", "/images/bergen-night.webp",
  "/images/vision-expo-v3.webp", "/images/vision-networking-v3.webp", "/images/vision-dinner-v4.webp", "/images/vision-broadcast.webp", ...TEAM_ASSETS, ...WHO_ASSETS,
];

export function VisionPrint() {
  return <DeckPrint slides={slidesFor(false)} fallback="" />;
}

export default function VisionDeck2({ mottaker = "", publicMode = false }) {
  return <DeckViewer deck="vision" slides={slidesFor(publicMode)} fallback={mottaker} filePrefix="Norwegian-AI-Summit-The-Next-Shift" assets={ASSETS} publicMode={publicMode} categoryField="variant" />;
}
